<template>
  <div class="activity-timeline">
    <SmallLoader v-if="smallLoading && !transactions.length" />
    <template v-else>
      <div v-if="timelineItems.length > 0" class="timeline-list">
        <div 
          v-for="(item, index) in timelineItems" 
          :key="item.id"
          class="timeline-item"
          @click="viewTransaction(item)"
        >
          <!-- Timeline connector -->
          <div class="timeline-connector">
            <div class="timeline-dot" :class="item.dotClass"></div>
            <div v-if="index < timelineItems.length - 1" class="timeline-line"></div>
          </div>

          <!-- Content -->
          <div class="timeline-content">
            <div class="content-header">
              <div class="transaction-icon" :class="item.iconClass">
                <svg v-if="item.isCredit" class="icon" viewBox="0 0 16 16" fill="none">
                  <path d="M8 12V4M8 4L4 8M8 4L12 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <svg v-else-if="item.isGift" class="icon" viewBox="0 0 16 16" fill="none">
                  <path d="M13 6H3V14H13V6Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M14 3H2V6H14V3Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M8 14V3" stroke="currentColor" stroke-width="1.5"/>
                  <path d="M8 3C8 3 8 1 6 1C4 1 4 3 6 3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                  <path d="M8 3C8 3 8 1 10 1C12 1 12 3 10 3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                </svg>
                <svg v-else class="icon" viewBox="0 0 16 16" fill="none">
                  <path d="M8 4V12M8 12L4 8M8 12L12 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
              <div class="transaction-info">
                <p class="transaction-type">{{ item.type }}</p>
                <p class="transaction-detail">{{ item.detail }}</p>
              </div>
            </div>
            <div class="content-meta">
              <span class="transaction-amount" :class="item.amountClass">{{ item.amount }}</span>
              <span class="transaction-time">{{ item.time }}</span>
            </div>
          </div>

          <!-- Status badge -->
          <div class="status-badge" :class="item.status.toLowerCase()">
            {{ item.status }}
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else class="empty-state">
        <div class="empty-icon">
          <svg viewBox="0 0 48 48" fill="none">
            <circle cx="24" cy="24" r="20" stroke="#E2E8F0" stroke-width="2"/>
            <path d="M24 14V26" stroke="#94A3B8" stroke-width="2" stroke-linecap="round"/>
            <circle cx="24" cy="32" r="1.5" fill="#94A3B8"/>
          </svg>
        </div>
        <p class="empty-text">No transactions yet</p>
      </div>
    </template>

    <!-- Transaction Modal -->
    <MediumModal
      v-if="showModal"
      @close="showModal = false"
    >
      <template v-slot:content>
        <div class="transaction-modal-content">
          <div class="modal-header">
            <h3 class="modal-title">Transaction Details</h3>
            <p class="modal-ref">{{ selectedTransaction?.reference }}</p>
          </div>
          <div class="modal-body">
            <div class="modal-row">
              <span class="modal-label">Type</span>
              <span class="modal-value">{{ selectedTransaction?.type }}</span>
            </div>
            <div class="modal-row">
              <span class="modal-label">Amount</span>
              <span class="modal-value">{{ selectedTransaction?.amount }}</span>
            </div>
            <div class="modal-row">
              <span class="modal-label">Date</span>
              <span class="modal-value">{{ selectedTransaction?.date }}</span>
            </div>
            <div class="modal-row">
              <span class="modal-label">Status</span>
              <span class="modal-value status" :class="selectedTransaction?.status?.toLowerCase()">
                {{ selectedTransaction?.status }}
              </span>
            </div>
          </div>
        </div>
      </template>
    </MediumModal>
  </div>
</template>

<script>
import MediumModal from "@/components/general/MediumModal.vue";
import { formatAmountToDollar, formatISODate, formatISOTime, removeUnderscore, truncate } from "@/utils/helpers.js";
import { mapActions, mapState } from "vuex";

export default {
  name: "ActivityTimeline",

  components: {
    MediumModal,
  },

  data() {
    return {
      showModal: false,
      selectedTransaction: null,
    };
  },

  computed: {
    ...mapState({
      smallLoading: (state) => state.smallLoading,
      transactions: (state) => state.organisationModule.transactions,
    }),

    timelineItems() {
      if (!this.transactions?.length) return [];
      
      return this.transactions.slice(0, 5).map((tx) => {
        const type = this.formatType(tx.type);
        const isCredit = this.isCreditTransaction(tx.type);
        const isGift = tx.type.includes('GIFT');
        
        let amount;
        if (tx.price) {
          amount = formatAmountToDollar(tx.price * tx.volume, 2, tx.token?.currency);
        } else {
          amount = formatAmountToDollar(tx.token?.price?.buy * tx.volume, 2, tx.token?.currency);
        }
        if (tx.type === "EXERCISE_RSU_RECEIVE") amount = "—";

        const tokenName = tx.token?.name?.includes("GetEquity") && tx.token?.name?.includes("Token") 
          ? tx.token.name.replace("GetEquity", "").replace("Token", "").trim()
          : tx.token?.name || '';

        return {
          id: tx._id,
          type: type,
          detail: `${tx.volume?.toLocaleString()} ${truncate(tokenName, 20)}`,
          amount: isCredit ? `+${amount}` : `-${amount}`,
          amountClass: isCredit ? 'credit' : 'debit',
          time: this.getRelativeTime(tx.createdAt),
          date: formatISODate(tx.createdAt),
          fullTime: formatISOTime(tx.createdAt),
          status: tx.status,
          reference: tx.reference,
          isCredit,
          isGift,
          iconClass: isGift ? 'gift' : isCredit ? 'credit' : 'debit',
          dotClass: tx.status?.toLowerCase(),
          raw: tx,
        };
      });
    },
  },

  created() {
    this.loadTransactions();
  },

  methods: {
    ...mapActions("organisationModule", ["getTransactions"]),

    async loadTransactions() {
      await this.getTransactions({ page: 1, limit: 5 });
    },

    formatType(type) {
      let formatted = removeUnderscore(type);
      switch (formatted) {
        case "Debit Sell Wallet": return "Debit Wallet";
        case "Fund Sell Wallet": return "Fund Wallet";
        default: return formatted;
      }
    },

    isCreditTransaction(type) {
      const creditTypes = ['MINT', 'FUND', 'CARRY', 'BUY', 'RECEIVE', 'EXIT', 'TRADE_REVERSAL', 'EXERCISE'];
      return creditTypes.some(t => type?.includes(t));
    },

    getRelativeTime(dateString) {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;
      return formatISODate(dateString);
    },

    viewTransaction(item) {
      this.selectedTransaction = {
        type: item.type,
        amount: item.amount.replace(/^[+-]/, ''),
        date: `${item.date} ${item.fullTime}`,
        status: item.status,
        reference: item.reference,
      };
      this.showModal = true;
    },
  },
};
</script>

<style lang="scss" scoped>
.activity-timeline {
  .timeline-list {
    display: flex;
    flex-direction: column;
  }

  .timeline-item {
    display: flex;
    align-items: flex-start;
    gap: 16px;
    padding: 16px 0;
    cursor: pointer;
    transition: background 0.15s ease;
    border-radius: 4px;
    margin: 0 -12px;
    padding-left: 12px;
    padding-right: 12px;

    &:hover {
      background: rgba(77, 132, 255, 0.04);
    }

    &:not(:last-child) {
      .timeline-line {
        display: block;
      }
    }
  }

  .timeline-connector {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding-top: 4px;
  }

  .timeline-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #94A3B8;
    flex-shrink: 0;

    &.completed { background: #10B981; }
    &.pending { background: #F59E0B; }
    &.failed { background: #EF4444; }
  }

  .timeline-line {
    width: 2px;
    flex: 1;
    min-height: 32px;
    background: #E2E8F0;
    margin-top: 8px;
    display: none;
  }

  .timeline-content {
    flex: 1;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
    min-width: 0;
  }

  .content-header {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
  }

  .transaction-icon {
    width: 36px;
    height: 36px;
    border-radius: 7px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    &.credit {
      background: #ECFDF5;
      color: #10B981;
    }

    &.debit {
      background: #FEF2F2;
      color: #EF4444;
    }

    &.gift {
      background: #EDE9FE;
      color: #7C3AED;
    }

    .icon {
      width: 18px;
      height: 18px;
    }
  }

  .transaction-info {
    min-width: 0;
  }

  .transaction-type {
    font-size: 14px;
    font-weight: 400;
    color: #0F172A;
    margin: 0 0 2px 0;
  }

  .transaction-detail {
    font-size: 13px;
    color: #64748B;
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .content-meta {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 2px;
    flex-shrink: 0;
  }

  .transaction-amount {
    font-size: 14px;
    font-weight: 400;

    &.credit { color: #10B981; }
    &.debit { color: #EF4444; }
  }

  .transaction-time {
    font-size: 12px;
    color: #94A3B8;
  }

  .status-badge {
    font-size: 10px;
    font-weight: 400;
    padding: 4px 8px;
    border-radius: 4px;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    flex-shrink: 0;

    &.completed {
      background: #ECFDF5;
      color: #10B981;
    }

    &.pending {
      background: #FEF3C7;
      color: #D97706;
    }

    &.failed {
      background: #FEF2F2;
      color: #EF4444;
    }
  }

  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    text-align: center;
  }

  .empty-icon {
    width: 48px;
    height: 48px;
    margin-bottom: 12px;
  }

  .empty-text {
    font-size: 14px;
    color: #64748B;
    margin: 0;
  }

  // Modal styles
  .transaction-modal-content {
    padding: 24px 20px 32px;

    @media (min-width: 640px) {
      padding: 24px 24px 32px;
    }
  }

  .modal-header {
    margin-bottom: 24px;
  }

  .modal-title {
    font-size: 18px;
    font-weight: 400;
    color: #0F172A;
    margin: 0 0 4px 0;
  }

  .modal-ref {
    font-size: 13px;
    color: #64748B;
    margin: 0;
  }

  .modal-body {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .modal-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 16px;
    border-bottom: 1px solid #F1F5F9;
  }

  .modal-label {
    font-size: 13px;
    color: #64748B;
  }

  .modal-value {
    font-size: 14px;
    font-weight: 400;
    color: #0F172A;

    &.status {
      padding: 4px 10px;
      border-radius: 4px;

      &.completed { background: #ECFDF5; color: #10B981; }
      &.pending { background: #FEF3C7; color: #D97706; }
      &.failed { background: #FEF2F2; color: #EF4444; }
    }
  }
}

@media (max-width: 640px) {
  .activity-timeline {
    .timeline-content {
      flex-direction: column;
      gap: 8px;
    }

    .content-meta {
      align-items: flex-start;
      flex-direction: row;
      gap: 12px;
    }

    .status-badge {
      display: none;
    }
  }
}
</style>
